import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { adminAuthApi } from '../services/api/authApi';
import apiClient from '../services/api/client';
import logger from '../utils/logger';
import PropTypes from 'prop-types';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

// localStorageキーの定義
const ADMIN_STORAGE_KEYS = {
  TOKEN: 'adminToken',
  REFRESH_TOKEN: 'adminRefreshToken',
  USER: 'adminUser',
  LAST_REFRESH: 'adminLastRefreshTime'
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [adminRefreshToken, setAdminRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  // 初期化：localStorageから認証情報を復元
  useEffect(() => {
    const initializeAuth = () => {
      setIsLoading(true);
      try {
        const storedToken = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
        const storedRefreshToken = localStorage.getItem(ADMIN_STORAGE_KEYS.REFRESH_TOKEN);
        const storedAdmin = localStorage.getItem(ADMIN_STORAGE_KEYS.USER);
        const storedLastRefresh = localStorage.getItem(ADMIN_STORAGE_KEYS.LAST_REFRESH);

        logger.debug('AdminAuthContext initialization', {
          hasToken: !!storedToken,
          hasRefreshToken: !!storedRefreshToken,
          hasAdmin: !!storedAdmin,
          keys: ADMIN_STORAGE_KEYS
        });

        if (storedToken && storedAdmin) {
          setAdminToken(storedToken);
          setAdminRefreshToken(storedRefreshToken);
          setAdmin(JSON.parse(storedAdmin));
          setLastRefreshTime(storedLastRefresh ? parseInt(storedLastRefresh) : null);

          // APIクライアントにトークンを設定
          apiClient.setAdminToken(storedToken);

          logger.userAction('ADMIN_AUTH_RESTORED', {
            adminId: JSON.parse(storedAdmin).id
          });
        }
      } catch (error) {
        logger.error('Failed to restore admin auth', { error });
        // clearAdminAuthはまだ定義されていないので、直接クリア処理を実行
        setAdmin(null);
        setAdminToken(null);
        setAdminRefreshToken(null);
        setLastRefreshTime(null);
        Object.values(ADMIN_STORAGE_KEYS).forEach((key) => {
          localStorage.removeItem(key);
        });
        apiClient.clearAdminToken();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // 認証情報をクリア
  const clearAdminAuth = useCallback(() => {
    setAdmin(null);
    setAdminToken(null);
    setAdminRefreshToken(null);
    setLastRefreshTime(null);

    // localStorageから削除
    Object.values(ADMIN_STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });

    // APIクライアントからトークンを削除
    apiClient.clearAdminToken();
  }, []);

  // 管理者ログイン
  const adminLogin = useCallback(
    async (credentials) => {
      try {
        setIsLoading(true);

        // 管理者認証APIを呼び出し
        const response = await adminAuthApi.adminLogin(credentials);

        // レスポンスから認証情報を取得
        // APIクライアントがcamelCaseに変換するため、両方の形式に対応
        const responseData = response.data || response;
        const access_token = responseData.access_token || responseData.accessToken;
        const refresh_token = responseData.refresh_token || responseData.refreshToken;
        const user = responseData.user;

        if (!access_token || !refresh_token || !user) {
          throw new Error('Invalid login response: missing required fields');
        }

        // システム管理者のレスポンスを確認
        // システム管理者（system_adminsテーブル）は通常ユーザーとは別体系なので
        // roleチェックは不要（APIが正しいエンドポイントで認証済み）

        // 状態を更新
        setAdmin(user);
        setAdminToken(access_token);
        setAdminRefreshToken(refresh_token);
        setLastRefreshTime(Date.now());

        // localStorageに保存
        localStorage.setItem(ADMIN_STORAGE_KEYS.TOKEN, access_token);
        localStorage.setItem(ADMIN_STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
        localStorage.setItem(ADMIN_STORAGE_KEYS.USER, JSON.stringify(user));
        localStorage.setItem(ADMIN_STORAGE_KEYS.LAST_REFRESH, Date.now().toString());

        // APIクライアントにトークンを設定
        apiClient.setAdminToken(access_token);

        logger.userAction('ADMIN_LOGIN_SUCCESS', {
          adminId: user.id,
          username: user.username
        });

        return { success: true, user };
      } catch (error) {
        logger.error('Admin login failed', { error });
        clearAdminAuth();
        return {
          success: false,
          error: error.response?.data?.detail || error.message || 'ログインに失敗しました'
        };
      } finally {
        setIsLoading(false);
      }
    },
    [clearAdminAuth]
  );

  // 管理者ログアウト
  const adminLogout = useCallback(async () => {
    try {
      // ログアウトAPIを呼び出し（オプション）
      if (adminToken) {
        await adminAuthApi.adminLogout();
      }
    } catch (error) {
      logger.error('Admin logout API call failed', { error });
    } finally {
      logger.userAction('ADMIN_LOGOUT', {
        adminId: admin?.id
      });
      clearAdminAuth();
    }
  }, [adminToken, admin, clearAdminAuth]);

  // トークンリフレッシュ
  const refreshAdminTokens = useCallback(async () => {
    if (!adminRefreshToken) {
      return false;
    }

    try {
      const response = await adminAuthApi.refreshAdminToken(adminRefreshToken);
      // APIクライアントがcamelCaseに変換するため、両方の形式に対応
      const access_token = response.access_token || response.accessToken;
      const refresh_token = response.refresh_token || response.refreshToken;

      // 新しいトークンを保存
      setAdminToken(access_token);
      setAdminRefreshToken(refresh_token);
      setLastRefreshTime(Date.now());

      localStorage.setItem(ADMIN_STORAGE_KEYS.TOKEN, access_token);
      localStorage.setItem(ADMIN_STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
      localStorage.setItem(ADMIN_STORAGE_KEYS.LAST_REFRESH, Date.now().toString());

      // APIクライアントにトークンを設定
      apiClient.setAdminToken(access_token);

      logger.userAction('ADMIN_TOKEN_REFRESHED', {
        adminId: admin?.id
      });

      return true;
    } catch (error) {
      logger.error('Admin token refresh failed', { error });
      clearAdminAuth();
      return false;
    }
  }, [adminRefreshToken, admin, clearAdminAuth]);

  // 自動トークンリフレッシュ（30分ごと）
  useEffect(() => {
    if (!adminToken || !adminRefreshToken) {
      return;
    }

    const REFRESH_INTERVAL = 30 * 60 * 1000; // 30分
    const CHECK_INTERVAL = 60 * 1000; // 1分ごとにチェック

    const checkAndRefresh = async () => {
      const now = Date.now();
      const timeSinceLastRefresh = lastRefreshTime ? now - lastRefreshTime : REFRESH_INTERVAL;

      if (timeSinceLastRefresh >= REFRESH_INTERVAL) {
        await refreshAdminTokens();
      }
    };

    const interval = setInterval(checkAndRefresh, CHECK_INTERVAL);

    // 初回チェック
    checkAndRefresh();

    return () => clearInterval(interval);
  }, [adminToken, adminRefreshToken, lastRefreshTime, refreshAdminTokens]);

  // APIクライアントのインターセプター設定
  useEffect(() => {
    if (!adminToken) {
      return;
    }

    // レスポンスインターセプターの設定
    const interceptor = apiClient.addAdminAuthInterceptor(adminToken, refreshAdminTokens, () => {
      clearAdminAuth();
    });

    return () => {
      // クリーンアップ
      apiClient.removeAdminAuthInterceptor(interceptor);
    };
  }, [adminToken, refreshAdminTokens, clearAdminAuth]);

  const isAuthenticated = !!admin && !!adminToken;

  // デバッグ用ログ（開発環境のみ）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('AdminAuthContext state', {
        isAuthenticated,
        hasAdmin: !!admin,
        hasToken: !!adminToken,
        isLoading,
        adminUsername: admin?.username
      });
    }
  }, [isAuthenticated, admin, adminToken, isLoading]);

  const value = {
    admin,
    adminToken,
    isLoading,
    isAuthenticated,
    adminLogin,
    adminLogout,
    refreshAdminTokens
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

AdminAuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AdminAuthContext;
